import { useEffect, useState } from 'react';
import type { OrgContractDocumentItem } from '@/modules/org/contracts/types/contract-document.types';
import { useGetDocxTemplateBySlug } from '@/modules/templates/hooks/use-templates';
import { buildDocxVariable } from '../utils';
import { mergeDocx } from '../merge-docx';
import type { FixedDocxDocument } from './document-payload';

interface Result {
  bytes: Uint8Array | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Resolve the .docx bytes to edit.
 *
 * Normally these come straight from the merged .docx stored at generation time.
 * Contracts created before that was persisted have no `docxUrl`, so they're
 * reconstructed by re-merging the template with the saved form values.
 */
export function useEditableDocxBytes({
  contract,
  document,
  slug,
}: {
  contract: OrgContractDocumentItem;
  document: FixedDocxDocument | null;
  slug: string;
}): Result {
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only the re-merge path needs the template, so don't fetch it otherwise.
  const needsTemplate = !document?.docxUrl;
  const { data: template } = useGetDocxTemplateBySlug(needsTemplate ? slug : undefined);

  const docxUrl = document?.docxUrl;

  useEffect(() => {
    let cancelled = false;
    setBytes(null);
    setError(null);

    (async () => {
      try {
        if (docxUrl) {
          const res = await fetch(docxUrl);
          if (!res.ok) throw new Error(`Failed to load document (${res.status})`);
          const loaded = new Uint8Array(await res.arrayBuffer());
          if (!cancelled) setBytes(loaded);
          return;
        }

        // Still waiting on the template for the re-merge path.
        if (!template) return;
        if (!template.docx?.tokenizedUrl) {
          throw new Error('This template is not available for editing.');
        }

        const values = JSON.parse(contract.FormData || '{}') as Record<string, string>;
        const merged = await mergeDocx({
          tokenizedUrl: template.docx.tokenizedUrl,
          variables: buildDocxVariable(template.variables),
          values,
        });
        if (!cancelled) setBytes(merged);
      } catch (err) {
        console.error('Failed to resolve editable .docx:', err);
        if (!cancelled) setError('Could not load the document for editing.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [docxUrl, template, contract.FormData]);

  return { bytes, error, isLoading: !bytes && !error };
}
